<template>
  <div
    class="module-container"
    :style="{
      'grid-column': `span ${gridColumnSpan}`,
      'grid-row': `span ${gridRowSpan}`
    }"
  >
    <h2 class="module-container__title">
      {{ title }}
    </h2>

    <div class="module-container__actions">
      <ReflectIconButton
        v-if="infoContent"
        icon="icon-info"
        @click="toggleInfoModal"
      />

      <ReflectLink
        v-if="isBenchmarkAvailable && benchmarkLink"
        :link="benchmarkLinkUrl"
        :query-params-to-keep="DASHBOARD_QUERY_PARAMS"
        :append="true"
        @click="openBenchmarkOnMeasure"
      >
        <ReflectIconButton icon="icon-bench" />
      </ReflectLink>

      <SettingsDataQualityButton
        check-mode="shared"
        :date-range="[selectedPeriod!.dateRange[0], selectedPeriod!.dateRange[1]]"
        :filters="allFilters"
        :member-titles="memberShortTitles"
      />

      <ReflectLink
        v-if="exploreLink"
        :link="exploreLinkUrl"
        :query-params-to-keep="DASHBOARD_QUERY_PARAMS"
        :query-params-to-add="exploreLinkQueryParamsToAdd"
        :append="true"
        @click="openExploreOnMeasure"
      >
        <ReflectIconButton
          :icon="`icon-${EXPLORER_TYPES_ICON[exploreLink.type!]}`"
        />
      </ReflectLink>
    </div>

    <div class="module-container__content">
      <slot />
    </div>

    <Teleport
      v-if="infoContent && isInfoModalOpen"
      to="#action"
    >
      <ReflectModal @cancel="toggleInfoModal">
        <template #title>
          <AnalyticsModuleInfoTitle
            :source-type="infoContent.source"
            :title="title"
            :default-title="defaultTitle"
          />
        </template>
        <template #default>
          <AnalyticsModuleInfo :content="infoContent" />
        </template>
      </ReflectModal>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import type { SlotsType } from 'vue'
import { TRACKING_EVENTS } from '~/constants/tracking'
import {
  type ModuleBenchmarkLink,
  type ModuleExplorerLink,
  type ModuleInfoContent
} from '~/types/analytics'
import { QUERY_PARAMETERS } from '~/types/queryParameters'
import { EXPLORER_TYPES_ICON } from '~/constants/renderTypes'
import { getAllRenamings } from '~/services/explore'
import { DASHBOARD_QUERY_PARAMS } from '~/constants/routes'
import { useTimeDimensionStore } from '~/stores/timeDimension'
import { useCustomizationStore } from '@/stores/customization'
import { useFiltersStore } from '~/stores/filters'

export default {
  name: 'ModuleContainer',
  props: {
    titleKey: {
      type: String,
      required: true
    },
    gridColumnSpan: {
      type: Number,
      default: 1
    },
    gridRowSpan: {
      type: Number,
      default: 1
    },
    infoContent: {
      type: Object as PropType<ModuleInfoContent | null>,
      default: null
    },
    exploreLink: {
      type: [Object, null] as PropType<ModuleExplorerLink | null>,
      default: null
    },
    benchmarkLink: {
      type: [Object, null] as PropType<ModuleBenchmarkLink | null>,
      default: null
    },
    memberShortTitles: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },
  setup() {
    return {
      EXPLORER_TYPES_ICON,
      DASHBOARD_QUERY_PARAMS
    }
  },
  data() {
    return {
      isInfoModalOpen: false
    }
  },
  slots: Object as SlotsType<{
    default: {}
  }>,
  computed: {
    ...mapState(useTimeDimensionStore, ['selectedPeriod']),
    ...mapState(useCustomizationStore, ['isBenchmarkAvailable']),
    ...mapState(useFiltersStore, ['allFilters']),
    exploreLinkUrl() {
      if (!this.exploreLink) {
        return ''
      }

      return `explore/${this.exploreLink.measure}`
    },
    exploreLinkQueryParamsToAdd(): {
      [key in QUERY_PARAMETERS]?: string | undefined
    } {
      if (!this.exploreLink) {
        return {}
      }

      return {
        [QUERY_PARAMETERS.EXPLORER_DIMENSIONS]: this.exploreLink.dimension
          ? encodeURIComponent(JSON.stringify([this.exploreLink.dimension]))
          : undefined,
        [QUERY_PARAMETERS.EXPLORER_GROUPS]: this.exploreLink.group
          ? encodeURIComponent(JSON.stringify([this.exploreLink.group]))
          : undefined,
        [QUERY_PARAMETERS.EXPLORER_TYPE]: this.exploreLink.type ?? undefined,
        [QUERY_PARAMETERS.EXPLORER_SORT]:
          this.exploreLink.sort !== undefined
            ? this.exploreLink.sort.toString()
            : undefined
      }
    },
    benchmarkLinkUrl() {
      if (!this.benchmarkLink) {
        return ''
      }
      return 'benchmark/' + this.benchmarkLink.measure
    },
    title() {
      return this.$t(this.titleKey, getAllRenamings())
    },
    defaultTitle() {
      return this.$t(this.titleKey, getAllRenamings(true))
    }
  },
  methods: {
    toggleInfoModal() {
      this.isInfoModalOpen = !this.isInfoModalOpen
      if (this.isInfoModalOpen) {
        this.$june.track(TRACKING_EVENTS.OPEN_MEASURE_DEFINITION, {
          measure: this.defaultTitle
        })
      }
    },
    openExploreOnMeasure() {
      this.$june.track(TRACKING_EVENTS.OPEN_MEASURE_EXPLORE, {
        measure: this.defaultTitle
      })
    },
    openBenchmarkOnMeasure() {
      this.$june.track(TRACKING_EVENTS.OPEN_MEASURE_BENCHMARK, {
        measure: this.defaultTitle
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.module-container {
  position: relative;
  padding: $margin-regular $margin-intermediate;
  display: flex;
  flex-direction: column;
  background-color: $bg-secondary;
  border-radius: 12px;
  @include block-shadow;

  &__title {
    @include font-text($font-weight-medium);
    @include font-size($font-size-normal, 28px);
    color: $text-secondary;
    display: flex;
    align-items: center;
    margin-bottom: 6px;
  }

  &__actions {
    display: none;
    position: absolute;
    top: $margin-regular;
    right: $margin-small;
    gap: 6px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__content > .module__row {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }

  &__content > .module__subtitle {
    display: flex;
    align-items: center;
    @include font-text($font-weight-medium);
    @include font-size($font-size-normal, 28px);
    color: $text-secondary;
    margin-top: $margin-regular;

    &:first-of-type {
      margin-top: $margin-mini;
    }
  }

  &:hover {
    .module-container__actions {
      display: flex;
    }
  }
}
</style>
